import { useMemo } from 'react';
import { useAppSelector } from '../redux';
import { userSelectors } from '../redux/user';
import { ISubNavigationStep } from '../types';
import { APP_ROUTES } from '../constants';
import {
  isShowEngagementBasedOnCountry,
  isSkipCityBasedOnCountry,
  isSkipEnglishLevelBasedOnCountry,
  isSkipSeniorityBasedOnExperience,
} from '../utils';
import { usePositionData } from './usePositionData';
import { useLPLocation } from './useLPLocation';
import { useVisitedSteps } from './useVisitedSteps';
import isNumber from 'lodash/isNumber';

export const useQuestioneerNavigationSteps = () => {
  const userData = useAppSelector(userSelectors.getUserData);
  const positionData = usePositionData();
  const { suggestedCountries } = useLPLocation();
  const { isStepVisited } = useVisitedSteps();

  const navigationSteps = useMemo(() => {
    const steps: ISubNavigationStep[] = [];
    const isSkipEnglishLevel = isSkipEnglishLevelBasedOnCountry(
      userData?.country,
    );

    const isSkipCity = isSkipCityBasedOnCountry(
      userData?.country,
      suggestedCountries,
    );

    const isSkipSeniority = isSkipSeniorityBasedOnExperience(
      userData?.yearsOfExperience,
    );
    const isShowEngagement = isShowEngagementBasedOnCountry(userData?.country);

    steps.push({
      label: 'Tell us about yourself',
      route: '',
      isHeader: true,
      isActive: true,
    });

    steps.push({
      label: 'Email',
      route: APP_ROUTES.EMAIL,
      isEditable: !!userData?.email || isStepVisited(APP_ROUTES.EMAIL),
    });

    steps.push({
      label: 'Your name',
      route: APP_ROUTES.NAME,
      isEditable: !!userData?.firstName || isStepVisited(APP_ROUTES.NAME),
    });

    steps.push({
      label: 'Country of residence',
      route: APP_ROUTES.COUNTRY,
      isEditable: !!userData?.country || isStepVisited(APP_ROUTES.COUNTRY),
    });

    if (!isSkipCity) {
      steps.push({
        label: 'City',
        route: APP_ROUTES.CITY,
        isEditable: !!userData?.city || isStepVisited(APP_ROUTES.CITY),
      });
    }

    if (!isSkipEnglishLevel) {
      steps.push({
        label: 'English',
        route: APP_ROUTES.ENGLISH_LEVEL,
        isEditable:
          !!userData?.englishLevel || isStepVisited(APP_ROUTES.ENGLISH_LEVEL),
      });
    }

    steps.push({
      label: 'Specialization and skills',
      route: APP_ROUTES.TECH_SKILLS,
      isEditable:
        !!userData?.specialization || isStepVisited(APP_ROUTES.TECH_SKILLS),
    });

    steps.push({
      label: 'Years of experience',
      route: APP_ROUTES.EXPERIENCE,
      isEditable:
        isNumber(userData?.yearsOfExperience) ||
        isStepVisited(APP_ROUTES.EXPERIENCE),
    });

    if (!isSkipSeniority) {
      steps.push({
        label: 'Seniority',
        route: APP_ROUTES.SENIORITY,
        isEditable:
          !!userData?.seniority || isStepVisited(APP_ROUTES.SENIORITY),
      });
    }

    if (isShowEngagement) {
      steps.push({
        label: 'Engagement',
        route: APP_ROUTES.ENGAGEMENT,
        isEditable:
          !!userData?.employmentType || isStepVisited(APP_ROUTES.ENGAGEMENT),
      });
    }

    steps.push({
      label: 'Compensation',
      route: APP_ROUTES.COMPENSATION,
      isEditable:
        isNumber(userData?.compensationExpectations) ||
        isStepVisited(APP_ROUTES.COMPENSATION),
    });

    steps.push({
      label: 'Notice period',
      route: APP_ROUTES.NOTICE_PERIOD,
      isEditable:
        isNumber(userData?.noticePeriod) ||
        isStepVisited(APP_ROUTES.NOTICE_PERIOD),
    });

    steps.push({
      label: 'Professional identity',
      route: APP_ROUTES.PROFESSIONAL_IDENTITY,
      isEditable:
        !!userData?.originalCvFileName ||
        isStepVisited(APP_ROUTES.PROFESSIONAL_IDENTITY),
    });

    return steps;
  }, [
    userData,
    positionData?.detectedPosition,
    suggestedCountries,
    isStepVisited,
  ]);

  return { navigationSteps };
};
